import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ChecklistRounded from '@mui/icons-material/ChecklistRounded';
import BoltRounded from '@mui/icons-material/BoltRounded';
import EventAvailableRounded from '@mui/icons-material/EventAvailableRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import HelpOutlineRounded from '@mui/icons-material/HelpOutlineRounded';
import ForumRounded from '@mui/icons-material/ForumRounded';
import SectionCard from './SectionCard';
import { buildDialOpTableHeadSx } from '../dialOpTableKit';
import { fetchDialOpLeadRoster } from '../../../../services/dialingOpportunityService';
import type { DialOpRosterResponse, DialOpRosterRow } from '../../../../types/dialingOpportunity';

/**
 * The roster carries no filters of its own. It is a forward-looking working
 * list anchored on the newest data in the warehouse, not a report over a chosen
 * window, so the page's period and state/agent controls stay on Dialing
 * performance where they mean something.
 */

const CALL_AT_STYLE: Record<
  string,
  { icon: React.ReactNode; color: 'error' | 'primary' | 'success' | 'default' }
> = {
  now: { icon: <BoltRounded sx={{ fontSize: 14 }} />, color: 'error' },
  window: { icon: <ScheduleRounded sx={{ fontSize: 14 }} />, color: 'primary' },
  appointment: { icon: <EventAvailableRounded sx={{ fontSize: 14 }} />, color: 'success' },
  unknown: { icon: <HelpOutlineRounded sx={{ fontSize: 14 }} />, color: 'default' },
};

function ScoreCell({ row }: { row: DialOpRosterRow }) {
  const unscored = row.score_band === 'Unscored';
  return (
    <Stack spacing={0.1}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: unscored ? 'text.disabled' : 'text.primary' }}>
        {row.score_band}
      </Typography>
      {row.score_pct != null ? (
        <Typography variant="caption" color="text.secondary">
          {row.score_pct}th percentile
        </Typography>
      ) : null}
    </Stack>
  );
}

/** Dial history is the reason a lead is still here, so it sits next to the lead. */
function AttemptSummary({ row }: { row: DialOpRosterRow }) {
  if (row.dials === 0) return <>Never dialed</>;
  if (row.ever_contacted) return <>{row.dials} dials · reached, not sold</>;
  return (
    <Box component="span" sx={{ color: row.dials >= 10 ? 'warning.main' : 'inherit' }}>
      {row.dials} dials · never reached
    </Box>
  );
}

/**
 * The compact form of the suggestion. Everything here is derived from data the
 * lead actually generated -- a reply they sent, where they stalled, or how
 * carriers have historically performed on their risk profile. The reasoning
 * behind each line lives in the expanded row.
 */
function SuggestionCell({ row }: { row: DialOpRosterRow }) {
  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        {row.has_inbound_sms ? (
          <ForumRounded sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />
        ) : null}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {row.talk_headline ?? '—'}
        </Typography>
      </Stack>
      {row.carrier_nm ? (
        <Typography variant="caption" color="text.secondary">
          Quote {row.carrier_nm} first
        </Typography>
      ) : null}
      {row.talk_facts.length ? (
        <Typography variant="caption" color="text.disabled">
          {row.talk_facts.join(' · ')}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default function LeadRosterSection() {
  const theme = useTheme();
  const [data, setData] = React.useState<DialOpRosterResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expanded, setExpanded] = React.useState<string | null>(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchDialOpLeadRoster({ limit: 500 })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setPage(0);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = data?.summary;
  const rows = data?.rows ?? [];
  const visible = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <SectionCard
      icon={<ChecklistRounded fontSize="small" color="action" />}
      title="Lead roster — who should call this lead, and when"
      description={
        summary
          ? `${summary.roster_size.toLocaleString()} leads from the last ${summary.window_days} days that haven't converted yet, ordered by when the next call should happen. ${summary.suppressed_count} do-not-call and bad-number leads were removed before this list was built.`
          : 'Leads from the last 7 days that have not converted yet, ordered by when the next call should happen.'
      }
      infoTooltip="Call At is shown in the lead's own timezone. It comes from the connect-rate model: the hour of day that historically reaches people in this lead's state, skipping hours already tried on this lead without success, kept inside normal calling hours, and spread across each state's best hours so one hour doesn't get every lead. Agent comes from who connects best on that state's leads over the last 8 weeks, shrunk toward the team average so a small lucky sample can't win, then balanced so every advisor gets a workable list. Suggested approach reads the lead's own inbound texts and where they stalled in the funnel to say what to open with, and ranks carriers by net commission through each policy's first 270 days on this lead's tobacco and BMI profile -- comparing carriers at the same age so a younger book can't look better just because its chargebacks haven't caught up yet. A carrier only gets suggested over the default once it has enough seasoned business to back the number; otherwise the default is kept. Leads with an appointment already booked keep their committed time."
    >
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {summary ? (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={`${summary.unreached_10plus.toLocaleString()} dialed 10+ times, never reached`}
          />
          <Chip size="small" variant="outlined" label={`${summary.never_dialed} never dialed`} />
          <Chip
            size="small"
            variant="outlined"
            label={`${summary.contacted_not_converted} reached, not sold`}
          />
          <Chip size="small" variant="outlined" label={`${summary.agents_available} advisors available`} />
          {summary.with_inbound_sms ? (
            <Chip
              size="small"
              color="success"
              variant="outlined"
              icon={<ForumRounded sx={{ fontSize: 14 }} />}
              label={`${summary.with_inbound_sms} replied by text`}
            />
          ) : null}
        </Stack>
      ) : null}

      {loading ? (
        <Stack alignItems="center" sx={{ py: 6 }} spacing={1.5}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Building the roster from live call history…
          </Typography>
        </Stack>
      ) : (
        <>
          <Table size="small">
            <TableHead sx={buildDialOpTableHeadSx(theme)}>
              <TableRow>
                <TableCell sx={{ width: 44 }} />
                <TableCell>Lead</TableCell>
                <TableCell>State</TableCell>
                <TableCell>Lead score</TableCell>
                <TableCell>Call at</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell sx={{ minWidth: 260 }}>Suggested approach</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((row) => {
                const key = String(row.hubspot_contact_id);
                const isOpen = expanded === key;
                const style = CALL_AT_STYLE[row.call_at_kind] ?? CALL_AT_STYLE.unknown;
                return (
                  <React.Fragment key={key}>
                    <TableRow
                      hover
                      onClick={() => setExpanded(isOpen ? null : key)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ py: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 700,
                            textDecoration: 'underline',
                            textUnderlineOffset: 2,
                          }}
                        >
                          Why
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.lead_label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.lead_sublabel ?? row.lead_ref} · <AttemptSummary row={row} />
                        </Typography>
                      </TableCell>
                      <TableCell>{row.state_cd}</TableCell>
                      <TableCell>
                        <ScoreCell row={row} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={style.icon as React.ReactElement}
                          color={style.color}
                          variant={row.call_at_kind === 'now' ? 'filled' : 'outlined'}
                          label={row.call_at_label}
                          sx={{ fontWeight: 600 }}
                        />
                        {row.call_at_detail ? (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                            {row.call_at_detail}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.agent_full_nm ?? '—'}
                        </Typography>
                        {row.status_label ? (
                          <Typography variant="caption" color="text.secondary">
                            {row.status_label}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell sx={{ py: 0.75 }}>
                        <SuggestionCell row={row} />
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <Stack
                            spacing={1.25}
                            sx={{
                              my: 1,
                              p: 1.75,
                              borderRadius: 1,
                              bgcolor: alpha(theme.palette.primary.main, 0.04),
                            }}
                          >
                            <Stack spacing={0.25}>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                Why this time
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                {row.call_at_reason}
                              </Typography>
                            </Stack>
                            <Stack spacing={0.25}>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                Why this agent
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                {row.agent_reason}
                                {row.last_agent_full_nm && row.last_agent_full_nm !== row.agent_full_nm
                                  ? ` Last attempt was by ${row.last_agent_full_nm}.`
                                  : ''}
                              </Typography>
                            </Stack>
                            {row.talk_detail ? (
                              <Stack spacing={0.25}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  What to open with
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                  {row.talk_detail}
                                </Typography>
                              </Stack>
                            ) : null}
                            {row.carrier_reason ? (
                              <Stack spacing={0.25}>
                                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                                  Which carrier to quote
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.55 }}>
                                  {row.carrier_reason} Carrier choice happens at application, so treat
                                  this as prep rather than a pre-call decision — and note it is
                                  correlational: advisors route cases using health disclosures this
                                  model cannot see.
                                </Typography>
                              </Stack>
                            ) : null}
                          </Stack>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
              {visible.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      No leads match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={rows.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            sx={{ '& .MuiTablePagination-toolbar': { fontSize: '0.75rem' } }}
          />
        </>
      )}
    </SectionCard>
  );
}
